import { motion, useReducedMotion } from 'motion/react';

import type { TipJarCopy } from './TipJar.types';
import * as styles from './TipJarCelebration.css';
import { textButton, tokens } from './tipJarTheme.css';

const CONFETTI_COLORS = [tokens.primary, tokens.faint, tokens.line];
const CONFETTI_COUNT = 14;
const CONFETTI_PIECES = Array.from({ length: CONFETTI_COUNT }, (_, index) => {
  const angle = (index / CONFETTI_COUNT) * Math.PI * 2;
  const distance = 64 + (index % 3) * 20;

  return {
    id: `piece-${index}`,
    x: Math.cos(angle) * distance,
    y: Math.sin(angle) * distance - 24,
    rotate: index * 47,
    color: CONFETTI_COLORS[index % CONFETTI_COLORS.length],
  };
});

type TipJarCelebrationProps = {
  copy: Pick<TipJarCopy, 'thanksHeading' | 'thanksBody' | 'chipInAgain'>;
  message: string;
  onChipInAgain: () => void;
};

export const TipJarCelebration = ({ copy, message, onChipInAgain }: TipJarCelebrationProps) => {
  const prefersReducedMotion = useReducedMotion();
  const writerMessage = message.trim();

  return (
    <motion.div
      role="status"
      className={styles.celebration}
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', stiffness: 260, damping: 24 }}
    >
      {!prefersReducedMotion && (
        <div className={styles.confetti} aria-hidden>
          {CONFETTI_PIECES.map((piece) => (
            <motion.span
              key={piece.id}
              className={styles.piece}
              style={{ background: piece.color }}
              initial={{ x: 0, y: 0, rotate: 0, scale: 0.4, opacity: 1 }}
              animate={{ x: piece.x, y: piece.y, rotate: piece.rotate, scale: 1, opacity: 0 }}
              transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
            />
          ))}
        </div>
      )}

      <motion.span
        className={styles.badge}
        initial={{ scale: 0.5 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 420, damping: 24 }}
        aria-hidden
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <motion.path
            d="M5 12.5l4.5 4.5L19 7.5"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 0.5, delay: 0.25, ease: 'easeOut' }}
          />
        </svg>
      </motion.span>

      <h2 className={styles.heading}>{copy.thanksHeading}</h2>
      {writerMessage ? (
        <blockquote className={styles.quote}>
          <p className={styles.message}>{writerMessage}</p>
        </blockquote>
      ) : (
        <p className={styles.body}>{copy.thanksBody}</p>
      )}
      <button type="button" className={textButton} onClick={onChipInAgain}>
        {copy.chipInAgain}
      </button>
    </motion.div>
  );
};
